import {
  Box,
  CardContent,
  Checkbox,
  Divider,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutline } from '@mui/icons-material';
import {
  ClassificationFlow,
  LpInvestorClassification,
} from 'further-types/lp/investor-classification';
import Switch from 'further-ui/components/Switch';
import FormField from 'further-ui/components/FormField';
import FormControlLabel from 'further-ui/components/FormControlLabel';
import { Column, Row } from 'further-ui/components/Layout';
import useInvestorAndReviewSettings from './useInvestorAndReviewSettings';
import ExperienceTestPassScoreField from './ExperienceTestPassScoreField';

const Container = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.sm),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.sm),
}));

const CheckboxGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(theme.spacingSizes.sm),
  flexWrap: 'wrap',
}));

const StyledCheckbox = styled(Checkbox)({
  paddingLeft: 0,
});

type Props = {
  vehicleId: string;
  title: string;
  classificationFlow: ClassificationFlow;
  flowSettings: {
    enabled: boolean;
    investorClassifications: Array<{
      id: LpInvestorClassification;
      name: string;
      isEnabled: boolean;
    }>;
    requiredTestResult: number | null;
    requireAllSelectedStatuses: boolean;
    requireInvestorExperienceTesting: boolean;
    requireInvestorKnowledgeTesting: boolean;
  };
};

const ClassificationCard: React.FC<Props> = ({
  vehicleId,
  title,
  classificationFlow,
  flowSettings,
}) => {
  const {
    allowFlowDisable,
    allowInvestorClassificationDisable,
    handleInvestorClassificationTypeToggle,
    handleInvestorClassificationFlowToggle,
    handleUpdateRequiredTestResult,
    handleRequireAllSelectedStatusesToggle,
    handleRequireInvestorExperienceTestingToggle,
    handleRequireInvestorKnowledgeTestingToggle,
  } = useInvestorAndReviewSettings(vehicleId);

  return (
    <Container>
      <Column spacing="xs" fullWidth>
        <Row fullWidth spacing="sm" justify="space-between" centered>
          <Typography variant="h6">{title} investors</Typography>
          <Switch
            disabled={flowSettings.enabled ? !allowFlowDisable : false}
            checked={flowSettings.enabled}
            onChange={(checked) => {
              handleInvestorClassificationFlowToggle(
                classificationFlow,
                checked,
              );
            }}
          />
        </Row>
        <Divider />
      </Column>
      <Column spacing="lg" fullWidth>
        <Column spacing="sm" fullWidth>
          <Typography variant="body2">
            Permitted {title} investor classifications
          </Typography>
          <CheckboxGroup>
            <Row spacing="md" flexWrap="wrap" paddingBottom={0.5}>
              {flowSettings.investorClassifications.map(
                (investorClassification) => (
                  <Row key={investorClassification.id} spacing="xxs" centered>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          variant: 'h6',
                        },
                      }}
                      control={
                        <StyledCheckbox
                          value={investorClassification.id}
                          disabled={
                            investorClassification.isEnabled
                              ? !allowInvestorClassificationDisable
                              : false
                          }
                          size="small"
                          checked={investorClassification.isEnabled}
                          onChange={(_, checked) => {
                            handleInvestorClassificationTypeToggle(
                              investorClassification.id,
                              checked,
                            );
                          }}
                        />
                      }
                      label={investorClassification.name}
                    />
                    {investorClassification.id ===
                    LpInvestorClassification.USQualifiedClient ? (
                      <Tooltip
                        title="The qualified client test is required if a fund manager: (i) wishes to accept accredited investors while also (ii) charging a performance fee or carried interest and (iii) does not fall into the size or venture capital regulation exemptions. This test does not show for qualified purchasers as they are, by default, considered qualified clients."
                        placement="top-start"
                      >
                        <InfoOutline fontSize="inherit" />
                      </Tooltip>
                    ) : null}
                  </Row>
                ),
              )}
            </Row>

            {classificationFlow === ClassificationFlow.US ? (
              <FormField
                labelColorLight
                spacing="xs"
                label="Require all selected statuses"
                tooltip="Select this if you require your investors to certify with all selected US statuses. If unselected, investors can pick between accredited investor and qualified purchaser status."
              >
                <Switch
                  checked={flowSettings.requireAllSelectedStatuses}
                  onChange={(checked) => {
                    handleRequireAllSelectedStatusesToggle(
                      classificationFlow,
                      checked,
                    );
                  }}
                  edge="start"
                />
              </FormField>
            ) : null}
            <FormField
              labelColorLight
              spacing="xs"
              label="Require investor experience testing"
            >
              <Switch
                checked={flowSettings.requireInvestorExperienceTesting}
                onChange={(checked) => {
                  handleRequireInvestorExperienceTestingToggle(
                    classificationFlow,
                    checked,
                  );
                }}
                edge="start"
              />
            </FormField>
            {flowSettings.requireInvestorExperienceTesting ? (
              <ExperienceTestPassScoreField
                requiredTestResult={flowSettings.requiredTestResult}
                title={`${title} ${
                  classificationFlow === ClassificationFlow.UK ||
                  classificationFlow === ClassificationFlow.US
                    ? 'investor experience'
                    : 'elective professional'
                } test pass score`}
                onBlur={(value) => {
                  handleUpdateRequiredTestResult(classificationFlow, value);
                }}
              />
            ) : null}
            <FormField
              labelColorLight
              spacing="xs"
              label="Require investor knowledge testing"
            >
              <Switch
                checked={flowSettings.requireInvestorKnowledgeTesting}
                onChange={(checked) => {
                  handleRequireInvestorKnowledgeTestingToggle(
                    classificationFlow,
                    checked,
                  );
                }}
                edge="start"
              />
            </FormField>
          </CheckboxGroup>
        </Column>
      </Column>
    </Container>
  );
};

export default ClassificationCard;
